import { useState } from "react";
import { generateAIPreview } from "../services/AIService";
import { useLeague } from "../context/LeagueContext";

function clipToLastSentence(text: string) {
  const lastPeriod = text.lastIndexOf(".");
  return lastPeriod !== -1 ? text.slice(0, lastPeriod + 1) : text;
}

export function useGameAIPrediction() {
  const {league} = useLeague();
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState<string | null>(null);

  const runPrediction = async (game: {
    homeTeamName: string;
    awayTeamName: string;
    hypeScores?: Record<string, number>;
    seriesInfo?: string;
    lastGameResult?: string;
    injuries?: string;
    news?: Record<string, string[]>;
    odds?: string;
  }) => {
    if (output) return;

    setLoading(true);

    const home = game.homeTeamName;
    const away = game.awayTeamName;
    const hypeA = String(game.hypeScores?.[home] ?? "N/A");
    const hypeB = String(game.hypeScores?.[away] ?? "N/A");

    const news = [
      ...(game.news?.[home] ?? []),
      ...(game.news?.[away] ?? []),
    ].join("; ") || "No major news";

    const isPlayoff = game.seriesInfo && /lead|tied|game\s?\d/i.test(game.seriesInfo);
    const gameType = isPlayoff ? "a playoff matchup" : "a regular season game";
    const currentLeague = league; // or NBA, NHL, etc.

    
    const prompt = `You are a veteran ESPN SportsCenter analyst. Write a sharp, informative, hype-infused preview of ${gameType} in the ${currentLeague}. Skip intros like “Alright folks” or “Buckle up.” Go straight into momentum analysis, betting angles, and key narratives.
    
    Your job is to break down the upcoming matchup between two teams. Use the following data to give fans betting insights, highlight recent momentum, and talk about how the series is shaping up. Mention recent games, key players, hype score differences, and any meaningful history or drama. Be bold, confident, and insightful. At the end, briefly predict who has the edge.
    
    Data:
    - League: MLB
    - Matchup: ${home} vs ${away}
    - HypeScore: ${home} ${hypeA}, ${away} ${hypeB}
    - Series: ${game.seriesInfo ?? "N/A"}
    - Last Game: ${game.lastGameResult ?? "N/A"}
    - Injuries: ${game.injuries ?? "None"}
    - News: ${news}
    - Odds: ${game.odds ?? "N/A"}
    
    Preview:`;
    

    try {
      const raw = await generateAIPreview(prompt);
      setOutput(clipToLastSentence(raw));
    } catch (e) {
      console.error("AI Prediction error:", e);
    } finally {
      setLoading(false);
    }
  };

  return { loading, output, runPrediction };
}
